import {
  BannerButton,
  GuideBannerConfig,
  OFF_LINE_STEP_NUMBER,
  PausedBannerConfig,
  TourConfig,
  TourStep,
} from '../../types';
import { TZ_PLUS_ID } from './ids';
import { pausedStepId, TZ_PLUS_COPY, TZ_PLUS_STOPS, TzPlusStop } from './stops';

/**
 * The TZ+ tour config (layer C), expanded mechanically from ./stops.ts.
 *
 * Each of the ten stops becomes three steps:
 *
 *   `n`            talking. Button 1 opens the clip, Button 2 skips it and moves to stop n+1.
 *   `n-watching`   idle and bubble-less, held while the clip modal is open.
 *   `n-close`      the "take your time" beat once the modal closes.
 *
 * The watching step exists precisely because `isReenterable` (core/backTarget.ts) is derived from
 * `ui.onMediaClose`: a step carrying it can never be returned to, so hanging it on the talking step
 * would make Go Back skip the whole habitat. Splitting it also keeps the talking banner's buttons
 * from sitting live behind the modal's backdrop.
 *
 * Stop 10 has no `10-close` — its clip closes straight onto the farewell (step `11`), which lives on
 * the same habitat page, so nothing navigates there.
 */

const clipUrl = (path: string, id: string): string => `${path}?tab=watch&id=${id}`;

const FIRST = TZ_PLUS_STOPS[0];
const LAST = TZ_PLUS_STOPS[TZ_PLUS_STOPS.length - 1];

const FAREWELL_STEP_ID = '11';
const COMPLETE_STEP_ID = 'complete';

/** Where the tour goes after stop n: the next stop's talking step, or the farewell after the last. */
const nextOf = (index: number): { stepId: string; stop?: TzPlusStop } => {
  const next = TZ_PLUS_STOPS[index + 1];
  return next ? { stepId: String(next.n), stop: next } : { stepId: FAREWELL_STEP_ID };
};

/** "Move on" button. Omits the path when the destination is the page we are already standing on. */
const advanceButton = (label: string, index: number, trackAction?: string): BannerButton => {
  const { stepId, stop } = nextOf(index);
  return {
    label,
    targetStepId: stepId,
    ...(stop && { navigatePath: stop.habitatPath, devNavigatePath: stop.devHabitatPath }),
    ...(trackAction && { trackAction }),
  };
};

/**
 * Go Back names its destination rather than popping the history stack.
 *
 * `goBack: true` exists for merge points, and it is the wrong tool here. A stop is reachable four
 * ways — the previous stop's skip button, that stop's clip-close beat, its paused step's "Skip
 * Ahead", and for stop 1 the welcome modal — and when the user arrives via a PAUSED step, popping
 * lands them back on it: `isReenterable` only excludes steps waiting on a media modal, so a paused
 * step stops the walk, and it has no guide variant for `stepHomePath` to read a page out of. The
 * user ends up standing in Tundra reading a banner that says "Jungle". The guided journey has the
 * same hole on all fourteen of its paused steps.
 *
 * Naming the target sidesteps all of it: back always means "the previous habitat", however the
 * user got here. The paused steps keep `goBack: true`, because resuming genuinely does have to
 * return to whichever step the break was taken from.
 */
const backToPreviousStop = (index: number): BannerButton => {
  const previous = TZ_PLUS_STOPS[index - 1];
  return {
    label: TZ_PLUS_COPY.goBack,
    targetStepId: String(previous.n),
    navigatePath: previous.habitatPath,
    devNavigatePath: previous.devHabitatPath,
    trackAction: 'went-back',
  };
};

/** Back within one habitat (the clip-close beat, and the farewell). No navigation needed. */
const backToOwnStop = (stop: TzPlusStop): BannerButton => ({
  label: TZ_PLUS_COPY.goBack,
  targetStepId: String(stop.n),
  trackAction: 'went-back',
});

const breakButton = (stop: TzPlusStop): BannerButton => ({
  label: TZ_PLUS_COPY.takeABreak,
  targetStepId: pausedStepId(stop),
  trackAction: 'paused',
});

/** Ends the tour from wherever the user paused. Quieter than Continue, and sits to its right. */
const finishButton: BannerButton = {
  label: TZ_PLUS_COPY.finish,
  completeTour: true,
  emphasis: 'secondary',
  trackAction: 'finished',
};

/**
 * The paused banner's controls, and the trailing one is an either/or: Skip Ahead while there is
 * still a habitat to skip to, Finish the Tour once there is not. The same absence of a next stop
 * also drops the NEXT HABITAT column and the track's second marker, so the whole banner stops
 * pointing forward at once.
 *
 * Continue is marked primary explicitly. At the last stop it is no longer the trailing button, and
 * the banner's default — "the last one is the filled one" — would otherwise light up Finish.
 */
const pausedButtons = (index: number, resume: BannerButton): BannerButton[] => {
  const hasNext = Boolean(nextOf(index).stop);
  return [
    ...(hasNext ? [advanceButton(TZ_PLUS_COPY.skipAhead, index, 'skipped-ahead')] : []),
    { ...resume, emphasis: 'primary' as const },
    ...(hasNext ? [] : [finishButton]),
  ];
};

/**
 * The paused catch-all every step falls through to when the user is not on its page. Resume here
 * navigates back to the stop's own page so the guide variant matches again — unlike the dedicated
 * paused STEP below, which pops the history stack instead.
 */
const pausedVariant = (stop: TzPlusStop, index: number): PausedBannerConfig => ({
  type: 'paused',
  currentSection: stop.section,
  ...(nextOf(index).stop && { nextHabitat: nextOf(index).stop!.section }),
  buttons: pausedButtons(index, {
    label: TZ_PLUS_COPY.resume,
    navigatePath: stop.habitatPath,
    devNavigatePath: stop.devHabitatPath,
    trackAction: 'resumed',
  }),
});

const analyticsFor = (stop: TzPlusStop, index: number, stepNumber: number) => {
  const { stop: next } = nextOf(index);
  return {
    stepNumber,
    section: stop.section,
    ...(next && { nextHabitat: next.section }),
  };
};

/** Prod path first, dev second — `stepHomePath` indexes `when[1]` to steer Go Back on `.live`. */
const whenOn = (stop: TzPlusStop): string[] => [stop.habitatPath, stop.devHabitatPath];

const stepsForStop = (stop: TzPlusStop, index: number): TourStep[] => {
  const { n } = stop;
  const talking = 3 * n - 2;
  const isLastStop = index === TZ_PLUS_STOPS.length - 1;

  const guide = (extra: Partial<GuideBannerConfig>): GuideBannerConfig => ({
    type: 'guide',
    mascot: 'talking',
    when: whenOn(stop),
    breakButton: breakButton(stop),
    ...extra,
  });

  const steps: TourStep[] = [
    {
      id: String(n),
      ui: {
        banner: [
          guide({
            speechBubble: stop.speech,
            buttons: [
              {
                label: stop.clipLabel,
                navigatePath: clipUrl(stop.habitatPath, stop.clipId),
                devNavigatePath: clipUrl(stop.devHabitatPath, stop.devClipId),
                targetStepId: `${n}-watching`,
              },
              advanceButton(stop.nextLabel, index),
            ],
            // No Go Back on the first stop: its only predecessor is the welcome modal, and
            // re-entering a modal mid-tour is a trap. Mirrors `Home 1` in the guided journey.
            ...(index > 0 && { backButton: backToPreviousStop(index) }),
          }),
          pausedVariant(stop, index),
        ],
      },
      analytics: analyticsFor(stop, index, talking),
    },
    {
      id: `${n}-watching`,
      ui: {
        // The clip closes onto the farewell at the last stop, per the flow deck.
        onMediaClose: { targetStepId: isLastStop ? FAREWELL_STEP_ID : `${n}-close` },
        banner: [
          // Silent and control-free: the modal covers this banner, so a bubble nobody can read and
          // buttons nobody can reach would only be there to be clicked by accident.
          { type: 'guide', mascot: 'idle', when: whenOn(stop) },
          pausedVariant(stop, index),
        ],
      },
      analytics: analyticsFor(stop, index, talking + 1),
    },
  ];

  if (!isLastStop) {
    steps.push({
      id: `${n}-close`,
      ui: {
        banner: [
          guide({
            speechBubble: TZ_PLUS_COPY.clipClose,
            buttons: [advanceButton(TZ_PLUS_COPY.clipCloseButton, index)],
            backButton: backToOwnStop(stop),
          }),
          pausedVariant(stop, index),
        ],
      },
      analytics: analyticsFor(stop, index, talking + 2),
    });
  }

  return steps;
};

/** One dedicated paused step per section, reached from a break button. Resume pops the stack. */
const pausedSteps: TourStep[] = TZ_PLUS_STOPS.map((stop, index) => ({
  id: pausedStepId(stop),
  ui: {
    banner: {
      type: 'paused',
      currentSection: stop.section,
      ...(nextOf(index).stop && { nextHabitat: nextOf(index).stop!.section }),
      buttons: pausedButtons(index, { label: TZ_PLUS_COPY.resume, goBack: true, trackAction: 'resumed' }),
    },
  },
  // Off the main line, so taking a break can never advance the furthest mark.
  analytics: { stepNumber: OFF_LINE_STEP_NUMBER, section: stop.section },
}));

export const tzPlusSteps: TourStep[] = [
  {
    id: '0-modal',
    ui: {
      modal: {
        kind: 'tzWelcome',
        // The modal's own action, so it navigates and reports like every other tour button. Step 1
        // stands in the giraffes' habitat, so the button has to travel — unlike the guided
        // journey's welcome, whose next step is simply the /home page it already sits on.
        button: {
          label: TZ_PLUS_COPY.welcomeButton,
          navigatePath: FIRST.habitatPath,
          devNavigatePath: FIRST.devHabitatPath,
          targetStepId: String(FIRST.n),
        },
      },
    },
    analytics: { stepNumber: 0 },
  },

  ...TZ_PLUS_STOPS.flatMap(stepsForStop),

  {
    id: FAREWELL_STEP_ID,
    ui: {
      banner: [
        {
          type: 'guide',
          mascot: 'talking',
          when: whenOn(LAST),
          speechBubble: TZ_PLUS_COPY.farewell,
          buttons: [{ label: TZ_PLUS_COPY.farewellButton, targetStepId: COMPLETE_STEP_ID }],
          backButton: backToOwnStop(LAST),
          breakButton: breakButton(LAST),
        },
        pausedVariant(LAST, TZ_PLUS_STOPS.length - 1),
      ],
    },
    analytics: {
      stepNumber: 3 * LAST.n,
      section: LAST.section,
    },
  },
  {
    // Terminal, and deliberately empty. Completing clears the tour and the arbiter drops it, so
    // anything rendered here would vanish mid-step — which is why the farewell banner advances to
    // a bare step rather than carrying `completesTour` itself.
    id: COMPLETE_STEP_ID,
    completesTour: true,
    ui: {},
    // One past the farewell. The tour's end is a position on the line like any other — without a
    // number it reports nothing, and the furthest mark stops one short of where the user got to.
    analytics: { stepNumber: 3 * LAST.n + 1, section: LAST.section },
  },

  ...pausedSteps,
];

export const tzPlusConfig: TourConfig = {
  id: TZ_PLUS_ID,
  steps: tzPlusSteps,
};
